import axios from "axios";
import { API_BASE_URL, buildHeaders } from "utils/services";
import { dayStart, dayEnd } from "pages/bBook/report/timezone";
import { freshParam } from "pages/bBook/cache";

export const fetchBBookStatus = async (user) => {
    try {
        const { data } = await axios.get(`${API_BASE_URL}/b-book/status`, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const fetchBBookExposure = async (user) => {
    try {
        const { data } = await axios.get(`${API_BASE_URL}/b-book/exposure`, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const updateBBookStatus = async (user, enabled) => {
    try {
        const { data } = await axios.post(`${API_BASE_URL}/b-book/status`, { enabled }, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const updateBBookExposure = async (user, low, high) => {
    try {
        const { data } = await axios.post(`${API_BASE_URL}/b-book/exposure`, { low, high }, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const fetchMandatoryABookUsers = async (user, page, limit, search) => {
    try {
        const params = `?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`
        const { data } = await axios.get(`${API_BASE_URL}/b-book/mandatory-a-book-users${params}`, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const addMandatoryABookUser = async (user, user_uuid) => {
    try {
        const { data } = await axios.post(`${API_BASE_URL}/b-book/mandatory-a-book-users`, { user_uuid }, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const removeMandatoryABookUser = async (user, user_uuid) => {
    try {
        const { data } = await axios.delete(`${API_BASE_URL}/b-book/mandatory-a-book-users/${user_uuid}`, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const fetchBBookTickers = async (user, search) => {
    try {
        const params = search ? `?search=${encodeURIComponent(search)}` : ''
        const { data } = await axios.get(`${API_BASE_URL}/b-book/tickers${params}`, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

// the asset's b-book-only leverages (margin rates below LMAX's), replaced as a whole; the ordinary ones are untouched
export const updateBBookTickerExtraLeverages = async (user, ticker, margin_rates) => {
    const { data } = await axios.put(`${API_BASE_URL}/b-book/tickers/${ticker.replaceAll(/[./]|%2F/g, '_')}/extra-leverages`, { margin_rates }, buildHeaders(user))
    return data
}

export const addBBookTicker = async (user, ticker) => {
    try {
        const { data } = await axios.post(`${API_BASE_URL}/b-book/tickers`, { ticker }, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const removeBBookTicker = async (user, ticker) => {
    try {
        const { data } = await axios.delete(`${API_BASE_URL}/b-book/tickers/${ticker.replaceAll(/[./]|%2F/g, '_')}`, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

// sort = { key, dir } picks the column the API orders the whole list by before cutting the page; none keeps the engine's order
const sortParams = (sort) => (sort?.key ? `&sort=${sort.key}&dir=${sort.dir}` : '')
// filters = { side, ticker }: the API keeps only that side and the tickers containing the text
const filterParams = (filters) => (filters?.side ? `&side=${filters.side}` : '') + (filters?.ticker ? `&ticker=${encodeURIComponent(filters.ticker)}` : '')

export const fetchBBookPositions = async (user, page, limit, sort, filters) => {
    try {
        const params = `?page=${page}&limit=${limit}${sortParams(sort)}${filterParams(filters)}`
        const { data } = await axios.get(`${API_BASE_URL}/b-book/positions${params}`, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const forcePositionToABook = async (user, position_id) => {
    try {
        const { data } = await axios.post(`${API_BASE_URL}/b-book/positions/${position_id}/force-a-book`, {}, buildHeaders(user))
        return data
    } catch (err) {
        console.log(err)
        throw err
    }
}

export const fetchBBookReturnablePositions = async (user, page, limit, sort, filters) => {
    const { data } = await axios.get(`${API_BASE_URL}/b-book/positions-returnable?page=${page}&limit=${limit}${sortParams(sort)}${filterParams(filters)}`, buildHeaders(user))
    return data
}

export const returnPositionToBBook = async (user, position_id) => {
    const { data } = await axios.post(`${API_BASE_URL}/b-book/positions/${position_id}/return-to-b-book`, {}, buildHeaders(user))
    return data
}

export const fetchBBookLimits = async (user) => {
    const { data } = await axios.get(`${API_BASE_URL}/b-book/limits`, buildHeaders(user))
    return data
}

// send only the fields that changed: the engine applies each limit independently
export const updateBBookLimits = async (user, limits) => {
    const { data } = await axios.post(`${API_BASE_URL}/b-book/limits`, limits, buildHeaders(user))
    return data
}

export const fetchBBookSpreads = async (user) => {
    const { data } = await axios.get(`${API_BASE_URL}/b-book/spreads`, buildHeaders(user))
    return data
}

export const updateBBookSpread = async (user, ticker, spread) => {
    const { data } = await axios.put(`${API_BASE_URL}/b-book/spreads/${ticker.replaceAll(/[./]|%2F/g, '_')}`, { spread }, buildHeaders(user))
    return data
}

export const fetchSpreadExemptUsers = async (user, page, limit, search) => {
    const params = `?page=${page}&limit=${limit}${search ? `&search=${encodeURIComponent(search)}` : ''}`
    const { data } = await axios.get(`${API_BASE_URL}/b-book/spread-exempt-users${params}`, buildHeaders(user))
    return data
}

export const addSpreadExemptUser = async (user, user_uuid) => {
    const { data } = await axios.post(`${API_BASE_URL}/b-book/spread-exempt-users`, { user_uuid }, buildHeaders(user))
    return data
}

export const removeSpreadExemptUser = async (user, user_uuid) => {
    const { data } = await axios.delete(`${API_BASE_URL}/b-book/spread-exempt-users/${user_uuid}`, buildHeaders(user))
    return data
}

// The days picked, as exact instants in the report's zone, plus the zone the server cuts its daily rows in.
const reportParams = ({ startDate, endDate }, zone) =>
    `?startDate=${dayStart(new Date(startDate), zone).toISOString()}&endDate=${dayEnd(new Date(endDate), zone).toISOString()}&tz=${encodeURIComponent(zone)}`

export const fetchBBookAbReport = async (user, dates, zone) => {
    const { data } = await axios.get(freshParam(`${API_BASE_URL}/b-book/ab-report${reportParams(dates, zone)}`), buildHeaders(user))
    return data
}

export const fetchBBookPnlReport = async (user, dates, zone) => {
    const { data } = await axios.get(freshParam(`${API_BASE_URL}/b-book/pnl-report${reportParams(dates, zone)}`), buildHeaders(user))
    return data
}

export const fetchBBookLive = async (user) => {
    const { data } = await axios.get(freshParam(`${API_BASE_URL}/b-book/live`), buildHeaders(user))
    return data
}